/**
 * Portal guided tours — training-style copy for click-through highlights.
 * Targets use [data-tour="..."] attributes in the UI.
 */

export const TOUR_VERSION = 'v1';

export const PORTAL_OVERVIEW_CHAPTERS = [
  { id: 'welcome', label: 'Welcome' },
  { id: 'start', label: 'Start' },
  { id: 'people', label: 'People' },
  { id: 'operations', label: 'Ops' },
  { id: 'compliance', label: 'Compliance' },
  { id: 'admin', label: 'Admin' },
];

const nav = (key, title, content, meta = {}) => ({
  target: `[data-tour="nav-${key}"]`,
  title,
  content,
  placement: 'right',
  ...meta,
});

export const portalOverviewSteps = [
  {
    target: '[data-tour="portal-main"]',
    title: 'Welcome to the Compliance Portal',
    content:
      'A quick guided tour of every area — built for day-to-day admin work and CQC readiness. Takes about three minutes.',
    placement: 'center',
    disableSpotlight: true,
    variant: 'welcome',
    chapter: 'welcome',
    icon: 'sparkles',
    estimatedMinutes: 3,
  },
  nav(
    'dashboard',
    'Dashboard — your control tower',
    'Start here each day. Counts for staff, recruitment, expiring documents, and tasks that need action — with links straight to the right page.',
    { chapter: 'start', icon: 'layout-dashboard', showChapterIntro: true, chapterIntro: 'Your morning starting point.' },
  ),
  nav(
    'employees',
    'Employees — active staff records',
    'Everyone who works for you has a profile here: documents, training, DBS, contracts, and compliance progress.',
    { chapter: 'people', icon: 'users', showChapterIntro: true, chapterIntro: 'Everyone you employ and support.' },
  ),
  nav(
    'recruitment',
    'Recruitment — applicants and onboarding',
    'New hires enter through recruitment before they become employees. Track applications, interviews, references, and readiness to start.',
    { chapter: 'people', icon: 'user-check' },
  ),
  nav(
    'service-users',
    'Service users — people you support',
    'The individuals receiving care. Link them to shifts, locations, and care plans so work is traceable for CQC and billing.',
    { chapter: 'people', icon: 'heart' },
  ),
  nav(
    'shifts',
    'Shifts — rota and attendance',
    'Create and assign shifts. Workers clock in/out on their phone. Approve attendance here before payroll.',
    { chapter: 'operations', icon: 'calendar-clock', showChapterIntro: true, chapterIntro: 'Rota, cover, and the pay pipeline.' },
  ),
  nav(
    'timesheets',
    'Timesheets — weekly pay bundles',
    'After approving attendance, generate a timesheet per worker for the pay week. Review hours, lock, then export CSV or PDF.',
    { chapter: 'operations', icon: 'receipt' },
  ),
  nav(
    'care-locations',
    'Care locations — where care happens',
    'Register homes and sites, manage QR codes for clock-in verification, and tie locations to organisations.',
    { chapter: 'operations', icon: 'map-pin' },
  ),
  nav(
    'organisations',
    'Organisations — councils and partners',
    'Councils and commissioning bodies live here. Staffing requests and invoicing trace back to an organisation.',
    { chapter: 'operations', icon: 'landmark' },
  ),
  nav(
    'staffing-requests',
    'Staffing requests — cover from councils',
    'When a council asks for cover, record the request here and convert it to a shift when you are ready.',
    { chapter: 'operations', icon: 'briefcase' },
  ),
  nav(
    'care-enquiries',
    'Care enquiries — new care leads',
    'Inbound enquiries from your website or office are triaged here before someone becomes a service user.',
    { chapter: 'operations', icon: 'heart-handshake' },
  ),
  nav(
    'compliance-hub',
    'Compliance Hub — policies and CQC',
    'Policies, incidents, complaints, and inspection insights — centralised for audits and CQC evidence.',
    { chapter: 'compliance', icon: 'building-2', showChapterIntro: true, chapterIntro: 'Evidence and inspection readiness.' },
  ),
  nav(
    'feedback',
    'Feedback — service user voice',
    'Compliments and concerns from service users and families are logged for governance and improvement.',
    { chapter: 'compliance', icon: 'message-square' },
  ),
  nav(
    'training',
    'Training — courses and expiry',
    'Assign mandatory training, track completion, and spot expiring certificates before they block someone from working.',
    { chapter: 'compliance', icon: 'graduation-cap' },
  ),
  nav(
    'dbs-register',
    'DBS register — criminal record checks',
    'Maintain DBS status for regulated roles. Prove safer recruitment at inspection.',
    { chapter: 'compliance', icon: 'shield' },
  ),
  nav(
    'audit',
    'Audit & inspection — evidence trail',
    'Every important action is logged. Use this when preparing for CQC. Inspection Mode limits what inspectors can change.',
    { chapter: 'compliance', icon: 'history' },
  ),
  nav(
    'admin-users',
    'Admin users — portal access',
    'Invite office staff and managers. Each person gets a role that controls what they can see and do.',
    { chapter: 'admin', icon: 'user-cog', showChapterIntro: true, chapterIntro: 'Settings, shortcuts, and next steps.' },
  ),
  nav(
    'settings',
    'Settings — organisation preferences',
    'Company details, handbook content, payroll labels, and other org-wide options used across the system.',
    { chapter: 'admin', icon: 'settings' },
  ),
  {
    target: '[data-tour="portal-search"]',
    title: 'Search',
    content: 'Find employees and records by name or reference — useful when the phone rings and you need a file fast.',
    placement: 'bottom',
    chapter: 'admin',
    icon: 'search',
  },
  {
    target: '[data-tour="add-employee-btn"]',
    title: 'Add employee',
    content: 'Shortcut to create a new staff record. For applicants still in recruitment, use the Recruitment page instead.',
    placement: 'bottom',
    chapter: 'admin',
    icon: 'user-plus',
  },
  {
    target: '[data-tour="portal-help-tour"]',
    title: "You're all set",
    content:
      'Reopen this tour anytime from Help. Ready for payroll? Run the weekly payroll tour, then: Shifts → approve → Timesheets → Lock → Export CSV or PDF.',
    placement: 'bottom',
    chapter: 'admin',
    icon: 'circle-check',
    variant: 'finish',
  },
];

export const auditorOverviewSteps = [
  {
    target: '[data-tour="portal-main"]',
    title: 'Auditor access',
    content: 'Your account is limited to read-only audit views. This short tour covers what you can access.',
    placement: 'center',
    disableSpotlight: true,
    variant: 'welcome',
    chapter: 'welcome',
    icon: 'eye',
    estimatedMinutes: 1,
  },
  nav(
    'dashboard',
    'Dashboard',
    'High-level indicators only. Follow links into Audit & Inspection for the full evidence trail.',
    { chapter: 'review', icon: 'layout-dashboard', showChapterIntro: true, chapterIntro: 'Read-only overview.' },
  ),
  nav(
    'audit',
    'Audit & inspection',
    'Primary workspace for reviewers: activity logs, exports, and inspection-pack style evidence.',
    { chapter: 'review', icon: 'history' },
  ),
  {
    target: '[data-tour="enter-inspection-mode-btn"]',
    title: 'Inspection mode',
    content: 'Switches the portal to a safer view for inspectors — fewer edit controls, clearer read-only presentation.',
    placement: 'bottom',
    chapter: 'review',
    icon: 'shield-check',
  },
  {
    target: '[data-tour="portal-help-tour"]',
    title: "You're all set",
    content: 'Reopen from Help → Run portal tour again whenever you need a refresher.',
    placement: 'bottom',
    chapter: 'review',
    icon: 'circle-check',
    variant: 'finish',
  },
];

export const AUDITOR_CHAPTERS = [
  { id: 'welcome', label: 'Welcome' },
  { id: 'review', label: 'Review' },
];

export const weeklyPayrollTourSteps = [
  {
    target: '[data-tour="portal-main"]',
    title: 'Weekly payroll prep',
    content: 'The pay path in order: approve worker times → bundle into timesheets → lock → export CSV or PDF.',
    placement: 'center',
    disableSpotlight: true,
    route: '/portal/shifts',
    variant: 'welcome',
    chapter: 'intro',
    icon: 'receipt',
    estimatedMinutes: 2,
  },
  {
    target: '[data-tour="shifts-attendance-review"]',
    title: 'Review submitted attendance',
    content:
      'Workers who have clocked out appear here. Check times and evidence, then Approve clean, Approve with warning, or Reject with a reason.',
    placement: 'top',
    route: '/portal/shifts',
    chapter: 'shifts',
    icon: 'clipboard-check',
    showChapterIntro: true,
    chapterIntro: 'Step 1 — approve hours.',
  },
  {
    target: '[data-tour="shifts-timesheet-ready"]',
    title: 'Timesheet-ready list',
    content:
      'Approved rows show payable times here. An “On timesheet” badge means they are already on a draft — do not pay twice.',
    placement: 'top',
    route: '/portal/shifts',
    chapter: 'shifts',
    icon: 'list-checks',
  },
  {
    target: '[data-tour="nav-timesheets"]',
    title: 'Open Timesheets',
    content: 'Bundle approved hours into pay-week documents. Click Next when ready — we will open the page.',
    placement: 'right',
    route: '/portal/timesheets',
    chapter: 'timesheets',
    icon: 'receipt',
    showChapterIntro: true,
    chapterIntro: 'Step 2 — bundle and export.',
  },
  {
    target: '[data-tour="timesheets-pay-period"]',
    title: 'Choose the pay week',
    content:
      'Set period start and end. Use “Fit to approved” if unsure — it matches your unprocessed approvals.',
    placement: 'bottom',
    route: '/portal/timesheets',
    chapter: 'timesheets',
    icon: 'calendar-range',
  },
  {
    target: '[data-tour="timesheets-generate"]',
    title: 'Generate drafts',
    content:
      'Creates one draft per worker. If you see “already on a timesheet”, void the old draft first or widen the dates.',
    placement: 'top',
    route: '/portal/timesheets',
    chapter: 'timesheets',
    icon: 'file-plus',
  },
  {
    target: '[data-tour="timesheets-detail"]',
    title: 'Check, lock, export',
    content:
      'Select each draft. Check hours and locations, click Lock when correct, then Export CSV (payroll software) or PDF (records). Locked timesheets cannot be changed.',
    placement: 'left',
    route: '/portal/timesheets',
    chapter: 'timesheets',
    icon: 'lock',
  },
  {
    target: '[data-tour="portal-help-tour"]',
    title: 'Weekly rhythm complete',
    content: 'That is the pay workflow. Reopen from Help → Run weekly payroll tour again when training a new admin.',
    placement: 'bottom',
    route: '/portal/timesheets',
    chapter: 'timesheets',
    icon: 'circle-check',
    variant: 'finish',
  },
];

export const PAYROLL_CHAPTERS = [
  { id: 'intro', label: 'Intro' },
  { id: 'shifts', label: 'Shifts' },
  { id: 'timesheets', label: 'Pay' },
];

export const TOUR_IDS = {
  overview: 'overview',
  payroll: 'payroll',
};

export function getChaptersForTour(tourId, { isAuditor } = {}) {
  if (tourId === TOUR_IDS.payroll) return PAYROLL_CHAPTERS;
  if (isAuditor) return AUDITOR_CHAPTERS;
  return PORTAL_OVERVIEW_CHAPTERS;
}

export function getChapterState(steps, stepIndex) {
  const step = steps[stepIndex];
  if (!step?.chapter) {
    return { chapters: [], activeChapterIndex: 0, progress: 0 };
  }

  const chapterOrder = [];
  steps.forEach((s) => {
    if (s.chapter && !chapterOrder.includes(s.chapter)) chapterOrder.push(s.chapter);
  });

  const activeChapterIndex = Math.max(0, chapterOrder.indexOf(step.chapter));
  const progress = steps.length > 1 ? ((stepIndex + 1) / steps.length) * 100 : 100;

  const chapters = chapterOrder.map((id) => {
    const def = PORTAL_OVERVIEW_CHAPTERS.find((c) => c.id === id)
      || AUDITOR_CHAPTERS.find((c) => c.id === id)
      || PAYROLL_CHAPTERS.find((c) => c.id === id);
    return { id, label: def?.label || id };
  });

  return { chapters, activeChapterIndex, progress };
}

export function getStepsForTour(tourId, { isAuditor }) {
  if (tourId === TOUR_IDS.payroll) return weeklyPayrollTourSteps;
  return isAuditor ? auditorOverviewSteps : portalOverviewSteps;
}